import './settings-leaf.css';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { companyApi } from '../../api/endpoints.ts';
import { Button, Alert } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
export default function WalkthroughPolicyCard() {
  const { company, can, refresh } = useSession();
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState<number | null>(company?.walkthroughPolicy?.petThreshold ?? null);
  const [revision, setRevision] = useState(company?.walkthroughPolicyRevision ?? 0);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scope = useRef(company?.id);
  const current = useRef(company?.id);
  current.current = company?.id;
  useEffect(() => {
    if (scope.current !== company?.id) {
      scope.current = company?.id;
      setDirty(false);
      setError(null);
      setThreshold(company?.walkthroughPolicy?.petThreshold ?? null);
      setRevision(company?.walkthroughPolicyRevision ?? 0);
    } else if (!dirty && (company?.walkthroughPolicyRevision ?? 0) >= revision) {
      setThreshold(company?.walkthroughPolicy?.petThreshold ?? null);
      setRevision(company?.walkthroughPolicyRevision ?? 0);
    }
  }, [company?.id, company?.walkthroughPolicy, company?.walkthroughPolicyRevision, dirty, revision]);
  async function save() {
    if (busy || !can('company:write')) return;
    const id = company?.id;
    setBusy(true);
    setError(null);
    try {
      const saved = await companyApi.update({
        walkthroughPolicy: { petThreshold: threshold },
        walkthroughPolicyRevision: revision,
      });
      if (current.current === id) {
        setThreshold(saved.walkthroughPolicy?.petThreshold ?? null);
        setRevision(saved.walkthroughPolicyRevision ?? 0);
        setDirty(false);
        // The PATCH snapshot remains authoritative if the session is offline.
        await refresh().catch(() => undefined);
      }
    } catch {
      if (current.current === id) setError(t('walkthrough.settingsError'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card work-section settings-leaf" aria-labelledby="walkthrough-settings-heading">
      <div className="work-section-heading">
        <h2 id="walkthrough-settings-heading" className="mb-3 text-lg font-semibold">
          {t('walkthrough.settingsHeading')}
        </h2>
      </div>
      <p className="mb-3">{t('walkthrough.settingsHint')}</p>
      <NumberInput
        label={t('walkthrough.threshold')}
        value={threshold}
        min={1}
        max={100}
        disabled={busy || !can('company:write')}
        onValueChange={(v) => {
          setThreshold(v);
          setDirty(true);
        }}
      />
      {can('company:write') && (
        <Button disabled={!dirty || busy} onClick={save}>
          {t('walkthrough.savePolicy')}
        </Button>
      )}
      {error && (
        <Alert tone="error">
          {error}
          <Button
            disabled={busy}
            onClick={async () => {
              await refresh();
              setDirty(false);
            }}
          >
            {t('walkthrough.refresh')}
          </Button>
        </Alert>
      )}
    </section>
  );
}
